import dayjs from "newui/utils/dayjs";
import { notificationsLang } from "../lang/en";
import type { NotificationDeliveryResource, NotificationDeliveryStatus } from "../types/Notifications.type";

export interface DeliveryAuditTableProps {
	deliveries: NotificationDeliveryResource[];
	isLoading: boolean;
}

const COLUMNS = [
	notificationsLang.auditColumnRecipient,
	notificationsLang.auditColumnType,
	notificationsLang.auditColumnChannel,
	notificationsLang.auditColumnShaping,
	notificationsLang.auditColumnStatus,
	notificationsLang.auditColumnReason,
	notificationsLang.auditColumnDispatchedAt,
];

const STATUS_LABEL: Record<NotificationDeliveryStatus, string> = {
	sent: notificationsLang.auditStatusSent,
	suppressed: notificationsLang.auditStatusSuppressed,
	failed: notificationsLang.auditStatusFailed,
};

const STATUS_CLASSNAME: Record<NotificationDeliveryStatus, string> = {
	sent: "text-emerald-700",
	suppressed: "text-amber-700",
	failed: "text-red-700",
};

const SkeletonRow = () => (
	<tr className="border-b border-gray-200">
		{COLUMNS.map((col) => (
			<td key={col} className="px-4 py-3">
				<div className="h-4 rounded bg-gray-200 animate-pulse" />
			</td>
		))}
	</tr>
);

const EmptyState = () => (
	<tr>
		<td colSpan={COLUMNS.length} className="px-4 py-12 text-center text-sm text-gray-500" data-testid="delivery-audit-empty">
			{notificationsLang.auditEmpty}
		</td>
	</tr>
);

// Rows are rendered exactly as returned by the API — hidden-project rows are
// simply not present in `deliveries` when the server excludes them (C16); this
// component never filters, counts, or placeholders anything client-side.
export const DeliveryAuditTable = ({ deliveries, isLoading }: DeliveryAuditTableProps) => {
	return (
		<div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
			<table data-testid="delivery-audit-table" className="min-w-full divide-y divide-gray-200">
				<thead className="bg-gray-50">
					<tr>
						{COLUMNS.map((col) => (
							<th
								key={col}
								className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap"
							>
								{col}
							</th>
						))}
					</tr>
				</thead>
				<tbody className="divide-y divide-gray-200 bg-white">
					{isLoading ? (
						<>
							<SkeletonRow />
							<SkeletonRow />
							<SkeletonRow />
						</>
					) : deliveries.length === 0 ? (
						<EmptyState />
					) : (
						deliveries.map((delivery) => (
							<tr key={delivery.id} data-testid={`delivery-audit-row-${delivery.id}`}>
								<td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{delivery.recipient}</td>
								<td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{delivery.type_key}</td>
								<td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{delivery.channel}</td>
								<td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{delivery.shaping}</td>
								<td className={`whitespace-nowrap px-4 py-3 text-sm font-medium ${STATUS_CLASSNAME[delivery.status]}`}>
									{STATUS_LABEL[delivery.status]}
								</td>
								<td className="px-4 py-3 text-sm text-gray-700" data-testid={`delivery-audit-reason-${delivery.id}`}>
									{delivery.reason ?? "—"}
								</td>
								<td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
									{dayjs(delivery.dispatched_at).format("DD MMM YYYY, h:mm A")}
								</td>
							</tr>
						))
					)}
				</tbody>
			</table>
		</div>
	);
};
